/**
 * Núcleo do cliente HTTP: montagem de URL, headers, refresh de sessão e o
 * `request` que todos os grupos de endpoint usam.
 *
 * Toda resposta de erro passa por `parseApiError`, que mapeia `code` -> copy
 * PT-BR + acao da tabela unica `ERROR_CODES` (RF-UX-059). Nenhuma copy de erro
 * e hardcoded nas telas.
 *
 * O refresh de sessão é transparente (D-14): 401 dispara `POST /auth/refresh`
 * uma vez e repete a requisição.
 */
import { meOutput } from "@zapir/shared";
import { z } from "zod";
import { parseApiError, ZapirApiError } from "../errors";
import { useSession } from "../store";

const BASE = "/api";

/* ---------------- Núcleo de request ---------------- */

interface RequestOptions<T> {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  // `any` no input: schemas com `.default()` tem Input != Output (ex.: searchResults).
  schema?: z.ZodType<T, z.ZodTypeDef, any>;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Ignora o header de tenant ativo (login/tenancy). */
  skipTenant?: boolean;
  /** Nao derruba a sessao em 401 (usado no proprio login). */
  skipAuthRedirect?: boolean;
}

function buildUrl(path: string, query?: RequestOptions<unknown>["query"]): string {
  const url = path.startsWith("/") ? path : `/${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export function authHeaders(): Record<string, string> {
  const { accessToken, tenantId } = useSession.getState();
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (tenantId) headers["X-Tenant-Id"] = tenantId;
  return headers;
}

/* ---------------- Refresh de sessão (D-14) ---------------- */

let refreshPromise: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  try {
    const response = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    if (!response.ok) return false;
    const payload = (await response.json().catch(() => null)) as { accessToken?: unknown; me?: unknown } | null;
    const me = meOutput.safeParse(payload?.me);
    if (typeof payload?.accessToken !== "string" || !me.success) return false;
    useSession.getState().setLogin(payload.accessToken, me.data);
    return true;
  } catch {
    return false;
  }
}

function refreshSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function request<T>(
  path: string,
  options: RequestOptions<T> = {},
  allowRefresh = true,
): Promise<T> {
  const { method = "GET", body, signal, schema, query, skipTenant, skipAuthRedirect } = options;
  const headers = authHeaders();
  if (!skipTenant) {
    const { tenantId } = useSession.getState();
    if (tenantId) headers["X-Tenant-Id"] = tenantId;
  } else {
    delete headers["X-Tenant-Id"];
  }
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(buildUrl(BASE + path, query), {
      method,
      headers,
      signal,
      credentials: "include",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    if (signal?.aborted) throw err;
    throw new ZapirApiError("SERVER_ERROR", 0, { network: true });
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    // Sessao expirada: renova uma unica vez e repete a requisicao (RF-TEN-002).
    if (response.status === 401 && allowRefresh && !skipAuthRedirect) {
      const refreshed = await refreshSession();
      if (refreshed) return request<T>(path, options, false);
    }
    const apiError = parseApiError(payload, response.status);
    if (apiError.code === "UNAUTHENTICATED" && !skipAuthRedirect) {
      useSession.getState().logout();
    }
    throw apiError;
  }

  if (schema) {
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      throw new ZapirApiError("SERVER_ERROR", response.status, {
        contract: true,
        issues: parsed.error.issues,
      });
    }
    return parsed.data;
  }
  return payload as T;
}
